import { Injectable } from "@angular/core";
import type { Capability } from "../model/action.types";
import type { DataContentType } from "../model/data.types";
import type { ContentBlockData } from "../model/block.types";
import { resolveLayerContract } from "../model/evidence-layer.types";
import { FeatureFlags } from "../../../viewer-components/model/feature-flags";
import { FeatureFlagsService } from "../../../viewer-components/services/internal-services/feature-flags.service";

const CAPABILITIES: Record<DataContentType, Capability[]> = {
  geojson: [
    { action: "zoom_to", label: "Zoom to", icon: "zoom_out_map", primary: true },
    { action: "add_layer", label: "Add layer", icon: "add_location_alt" },
    { action: "view_table", label: "View table", icon: "table_chart" },
    { action: "view_data", label: "View data", icon: "data_object" },
    { action: "download", label: "Download", icon: "download" },
    { action: "copy", label: "Copy", icon: "content_copy" },
  ],
  image: [
    { action: "view_data", label: "View", icon: "open_in_full", primary: true },
    { action: "download", label: "Download", icon: "download" },
    { action: "copy", label: "Copy", icon: "content_copy" },
  ],
  geotiff: [
    { action: "add_layer", label: "Add layer", icon: "add_location_alt" },
    // download works for endpoint-card (blobUrl); SSE-based geotiff download is handled by the inline viewer button
    { action: "download", label: "Download", icon: "download", primary: true },
    { action: "copy", label: "Copy", icon: "content_copy" },
  ],
  json: [
    { action: "view_data", label: "View data", icon: "data_object", primary: true },
    { action: "copy", label: "Copy", icon: "content_copy" },
    { action: "download", label: "Download", icon: "download" },
  ],
  text: [
    { action: "copy", label: "Copy", icon: "content_copy", primary: true },
    { action: "download", label: "Download", icon: "download" },
  ],
  pdf: [
    { action: "download", label: "Download", icon: "download", primary: true },
    { action: "view_data", label: "View", icon: "open_in_full" },
  ],
  url: [{ action: "open_url", label: "Open", icon: "open_in_new", primary: true }],
  binary: [{ action: "download", label: "Download", icon: "download", primary: true }],
  zip: [{ action: "download", label: "Download", icon: "download", primary: true }],
  unknown: [],
};

/**
 * Returns the capabilities available for a given data content type,
 * filtered by debug mode for debugOnly capabilities.
 */
@Injectable({ providedIn: "root" })
export class CapabilityRegistry {
  /**
   * Returns capabilities for the given data type, optionally including debugOnly entries.
   * Pass the current debug mode value from the calling component.
   */
  getCapabilities(type: DataContentType, debugMode = false): Capability[] {
    const all = CAPABILITIES[type] ?? [];
    return all.filter((c) => !c.debugOnly || debugMode);
  }

  /**
   * Returns all capabilities including debugOnly entries.
   * The caller is responsible for filtering based on debug mode.
   */
  getAllCapabilities(type: DataContentType): Capability[] {
    return CAPABILITIES[type] ?? [];
  }
}

/**
 * Viewer-handoff capability for agent-produced results (viewer-handoff plan F1): a block whose
 * `data.layer.type` is `"embeddings"` / `"elements"` gains an action that pushes the block's
 * ref GeoJSON into the matching viewer component (Search Enhanced Results / Custom Elements).
 * Each handoff is gated by the same feature flag that gates its target toolbar
 * (`FeatureFlagsService`, the map-container mechanism). When the handoff is added it takes
 * over as the card's single `primary` action, demoting the base list's own primary into the
 * ⋯ menu (max 1 primary per card). Without a matching type/flag, `base` passes through
 * untouched.
 */
export function layerHandoffCapabilities(
  block: ContentBlockData,
  featureFlags: FeatureFlagsService,
  base: Capability[] = [],
): Capability[] {
  const type = resolveLayerContract(block)?.type;
  const handoff: Capability | undefined =
    type === "embeddings" && featureFlags.hasFlag(FeatureFlags.ENHANCED_GEO_SEARCH)
      ? { action: "open_in_embeddings", label: "Open in Embeddings", icon: "travel_explore", primary: true }
      : type === "elements" && featureFlags.hasFlag(FeatureFlags.CUSTOM_ELEMENTS_DETECTION)
        ? { action: "open_in_elements", label: "Open in Elements", icon: "category", primary: true }
        : undefined;
  if (!handoff) return base;
  return [...base.map((c) => (c.primary ? { ...c, primary: false } : c)), handoff];
}
